import React from "react";

import AgentConfigurationHelpPanel from "./help/AgentConfigurationHelpPanel";
import CachedMessagesHelpPanel from "./help/CachedMessagesHelpPanel";
import CartHelpPanel from "./help/CartHelpPanel";
import ChatHelpPanel from "./help/ChatHelpPanel";
import ChatHistoryHelpPanel from "./help/ChatHistoryHelpPanel";
import ExportLocationsHelpPanel from "./help/ExportLocationsHelpPanel";
import MetadataConfigurationHelpPanel from "./help/MetadataConfigurationHelpPanel";
import UserAnalyticsHelpPanel from "./help/UserAnalyticsHelpPanel";
import UserShareAccessHelpPanel from "./help/UserShareAccessHelpPanel";
import ProcessingStepsPanel from "./split/ProcessingStepsPanel";

export interface RouteContent {
  helpPanel?: {
    header: string;
    content: React.ReactNode;
  };
  splitPanel?: {
    header: string;
    content: React.ReactNode;
  };
}

export const routeContentMap: Record<string, RouteContent> = {
  "/": {
    helpPanel: {
      header: "Chat Help",
      content: <ChatHelpPanel />,
    },
    splitPanel: {
      header: "Processing Steps",
      content: <ProcessingStepsPanel />,
    },
  },
  "/cart": {
    helpPanel: {
      header: "Cart Help",
      content: <CartHelpPanel />,
    },
  },
  "/admin/cached-messages": {
    helpPanel: {
      header: "Cached Messages Help",
      content: <CachedMessagesHelpPanel />,
    },
  },
  "/admin/chat-history": {
    helpPanel: {
      header: "Chat History Help",
      content: <ChatHistoryHelpPanel />,
    },
  },
  "/admin/metadata-configuration": {
    helpPanel: {
      header: "Metadata Configuration Help",
      content: <MetadataConfigurationHelpPanel />,
    },
  },
  "/admin/agent-configuration": {
    helpPanel: {
      header: "Agent Configuration Help",
      content: <AgentConfigurationHelpPanel />,
    },
  },
  "/admin/evaluation": {
    helpPanel: {
      header: "Evaluation Help",
      content: (
        <>
          <p>
            The Evaluation Runner executes the full eval dataset (153 cases)
            against the deployed agent Lambda and scores it on SQL validity,
            execution accuracy, conversational quality, and other metrics.
          </p>
          <p>
            A typical run takes 20-25 minutes. Results are stored in S3 and
            the verdict (Pass/Fail) is determined by configurable thresholds.
          </p>
        </>
      ),
    },
  },
  "/admin/eval-dataset": {
    helpPanel: {
      header: "Agent Evaluation Dataset",
      content: (
        <>
          <h3>What is this?</h3>
          <p>
            This is the test suite for the MAMBA agent. Each row is a question
            the agent will be asked during automated evaluation runs. The
            agent's response is scored against your expected answer.
          </p>
          <h3>Adding a test case</h3>
          <p>
            Click <strong>Add case</strong> and provide a question and the
            expected answer. A Test ID is generated automatically. SQL is
            optional — leave it blank if you just want to test answer quality.
          </p>
          <h3>Key fields</h3>
          <ul>
            <li>
              <strong>Difficulty</strong> — groups results in the report and
              sets pass/fail thresholds. Does not change how the agent answers.
            </li>
            <li>
              <strong>Out of Scope</strong> — marks questions the agent should
              refuse (e.g. non-sports topics). Used to test guardrails.
            </li>
            <li>
              <strong>Disabled</strong> — temporarily excludes a case from runs
              without deleting it.
            </li>
          </ul>
          <h3>When do changes take effect?</h3>
          <p>
            Immediately on the next evaluation run — no code deploy needed.
            Evaluations run daily (smoke) and weekly (full).
          </p>
        </>
      ),
    },
  },
  "/admin/export-locations": {
    helpPanel: {
      header: "Export & Transfer Locations Help",
      content: <ExportLocationsHelpPanel />,
    },
  },
  "/admin/user-analytics": {
    helpPanel: {
      header: "User Analytics Help",
      content: <UserAnalyticsHelpPanel />,
    },
  },
  "/admin/users": {
    helpPanel: {
      header: "User Share Access Help",
      content: <UserShareAccessHelpPanel />,
    },
  },
};

export const getRouteContent = (pathname: string): RouteContent =>
  routeContentMap[pathname] || {};
